import { useNavigate } from "react-router-dom";
import {
  debounce,
  Divider,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Switch,
  Popover,
  Box,
} from "@mui/material";
import {
  useState,
  SyntheticEvent,
  ChangeEvent,
} from "react";
import { useQuery, useMutation } from "react-query";
import usePopup from "../../../hooks/usePopup";
import moduleRoutes from "../../../routes/moduleRouteList";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import mastersRouteList from "../../routeList";
import { SearchParamsType, MasterTableDataType } from "./types";
import { getMastersTableData, saveMasterData } from "./services";
import EditIcon from "@mui/icons-material/Edit";
import clinicMasterRouteList from "../routeList";
import useAuthentication from "@hooks/useAuthentication";
const pageTitle = "Category ";
const base = (window as any)._env_?.BASE_URL;
const SubDepartmentMaster = () => {
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const [searchParams, setSearchParams] = useState<SearchParamsType>({
    isActive: 2,
    searchText: "",
    deptId: 0,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedCptRow, setSelectedCptRow] = useState<MasterTableDataType | null>(
    null,
  );
  const { showSnackbar } = usePopup();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const {
    data: tableData,
    isFetching: tableDataLoading,
    refetch,
  } = useQuery(
    [`${pageTitle}masterlist`, searchParams],
    async () => getMastersTableData(searchParams),
    {
      refetchOnWindowFocus: false,
      enabled: true,
    }
  );

  const { mutate: updateStatus, isLoading } = useMutation(saveMasterData, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        refetch();
        setSelectedId(null);
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message: e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const handleStatusChange = (
    _: SyntheticEvent,
    value: { label: string; value: number } | null
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      isActive: Number(value?.value),
    }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchParams((prev) => ({
      ...prev,
      searchText: e.target.value,
    }));
  };

  const debounceSearch = debounce(handleSearchChange, 500);

  const handleNavigateClick = (data?: MasterTableDataType) => {
    navigate(
      `${base}${moduleRoutes.mainRoute}/${moduleRoutes.masters}/${mastersRouteList.clinicMaster}/${clinicMasterRouteList.category}/${clinicMasterRouteList.addOrEdit}`,
      { state: data || null }
    );
  };

  const handleUpdateOneStatus = (
    status: boolean,
    data: MasterTableDataType
  ) => {
    setSelectedId(data.subDeptId);
    updateStatus({
      categoryId: data?.categoryId || null,
      categoryName: data.categoryName,
      clinicId: Number(user?.clinicId),
      cptId: data.cpt?.map((i) => i.cptId) || [],
      description: data.description,
      specimenId: data?.specimenId || null,
      subDeptId: data.subDeptId,
      isActive: status ? 1 : 0,
    });
  };

  const handleShowCpt = (
    event: React.MouseEvent<HTMLElement>,
    tableItem: MasterTableDataType,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedCptRow(tableItem);
  };

  const handleCloseCpt = () => {
    setAnchorEl(null);
    setSelectedCptRow(null);
  };

  return (
    <div className="p-3">
      <Paper>
        <div className="flex justify-between p-3">
          <div className="flex gap-2">
            <Typography variant="h6" color={"default"}>
              {pageTitle} Master
            </Typography>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                handleNavigateClick();
              }}
            >
              new {pageTitle}
            </Button>
          </div>
        </div>
        <Divider />
        <div className="">
          <TableContainer className="border">
            <div className="grid grid-cols-4 gap-3 py-3 px-2">
              <Autocomplete
                disableClearable
                fullWidth
                defaultValue={
                  selectedStatus[
                    searchParams.isActive as keyof typeof selectedStatus
                  ]
                }
                options={statusOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField {...params} label="Status" />
                )}
                onChange={handleStatusChange}
              />
              <TextField label={"Search"} onChange={debounceSearch} />
            </div>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sl no</TableCell>
                  {headings.map((heading) => (
                    <TableCell key={heading.key} className="font-bold">
                      {heading.label}
                    </TableCell>
                  ))}
                  <TableCell className="font-bold">Status</TableCell>
                  <TableCell className="font-bold">Actions</TableCell>
                </TableRow>
              </TableHead>
              <CustomTableBody
                alert={"No data"}
                dataLoading={tableDataLoading}
                isData={Boolean(tableData?.response?.length)}
                totalSpan={{
                  row: Number(tableData?.response?.length || 10),
                  col: headings.length + 2,
                }}
              >
                {tableData?.response?.map((tableItem, index) => (
                  <TableRow hover key={`${pageTitle}-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    {headings.map((heading) => (
                      <TableCell
                        key={heading.key}
                        onClick={(e) => handleShowCpt(e, tableItem)}
                        className="cursor-pointer"
                      >
                          {(() => {
                            const value =
                              tableItem[heading.key as keyof typeof tableItem];

                            if (
                              typeof value === "string" ||
                              typeof value === "number"
                            ) {
                              return value;
                            }

                            if (Array.isArray(value)) {
                              return value
                                .map((item) => item.cptName)
                                .join(", ");
                            }

                            return ""; // handles null or other types
                          })()}
                        </TableCell>
                      ))}

                      <TableCell
                        onClick={(e) => handleShowCpt(e, tableItem)}
                        className="cursor-pointer"
                      >
                        {tableItem.isActive == 1 ? (
                          <div className="text-green-600 text-center rounded-[10px] w-[70px] font-semibold">
                            Active
                          </div>
                        ) : (
                          <div className="text-red-600 text-center rounded-[10px] w-[70px] font-semibold">
                            Inactive
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              handleNavigateClick(tableItem);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <div className="flex justify-center items-center gap-2">
                            <Switch
                              checked={Boolean(tableItem.isActive)}
                              disabled={
                                isLoading && tableItem.categoryId == selectedId
                              }
                              onChange={(e) => {
                                handleUpdateOneStatus(
                                  e.target.checked,
                                  tableItem
                                );
                              }}
                            />
                            {isLoading &&
                              tableItem.categoryId == selectedId && (
                                <CircularProgress size={18} />
                              )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                ))}
              </CustomTableBody>
            </Table>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseCpt}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  width: 320,
                  padding: 0,
                },
              }}
            >
              <div className="space-y-2">
                <div className="p-2 border-b">
                  <Typography fontWeight={500}>CPT</Typography>
                </div>
                <div className="p-2 space-y-2 max-h-[50vh] overflow-y-auto">
                  {selectedCptRow?.cpt?.map((i) => (
                    <Box
                      key={i.cptId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">{i.cptName}</Typography>
                    </Box>
                  ))}
                </div>
              </div>
            </Popover>
            <div className="flex justify-end"></div>
          </TableContainer>
        </div>
      </Paper>
    </div>
  );
};

export default SubDepartmentMaster;

const headings = [
  {
    label: "Category name",
    key: "categoryName",
  },
  {
    label: "Sub department",
    key: "subDeptName",
  },
  {
    label: "Description",
    key: "description",
  },
];
const statusOptions = [
  { label: "All", value: 2 },
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];
const selectedStatus = {
  2: { label: "All", value: 2 },
  1: { label: "Active", value: 1 },
  0: { label: "Inactive", value: 0 },
};
